import { parseAdjustStockForm } from './adjustStockCommand';
import { parseCorrectStockCostForm } from './correctStockCostCommand';
import { canOpenBulkCoefficient, canOpenPhysicalCount, parseBulkApplyScope, parseBulkPreviewScope, parsePhysicalCountApply } from './bulkCoefficientCommand';
import { buildHistoryFilters } from './inventoryHistoryCore';
import type {
  InventoryFlowResult,
  InventoryEditContext,
  ReceiveStockForm,
  AdjustStockForm,
  CorrectStockCostForm,
  TransferStockForm,
  BulkApplyScope,
  BulkPreviewScope,
  PhysicalCountRow,
  HistoryFilterForm,
  StockRow,
  TransferRow,
} from './inventoryOperations.types';
import type { InventoryOperationsPort } from './inventoryOperationsPort';
import { buildPhysicalCountRows, diffPhysicalCountRows } from './physicalCountCore';
import type { GetStockByBranchOptions, VariantStockCellRow } from '../stock/public-api';
import { parseReceiveStockForm } from './receiveStockCommand';
import { canOpenTransferEntry, parseTransferStockForm } from './transferStockCommand';

export class InventoryOperationsController {
  constructor(private readonly port: InventoryOperationsPort) {}

  parseReceiveStock(ctx: InventoryEditContext, form: ReceiveStockForm) {
    return parseReceiveStockForm(ctx, form);
  }

  async runReceiveStock(
    ctx: InventoryEditContext,
    form: ReceiveStockForm
  ): Promise<InventoryFlowResult> {
    const parsed = this.parseReceiveStock(ctx, form);
    if (parsed.kind === 'blocked') return parsed;
    try {
      await this.port.receiveStock({ ...parsed.data!, performedByUserId: ctx.userId, variantId: parsed.data!.variantId });
      return {
        kind: 'done',
        refresh: parsed.refresh,
        notice: { tone: 'success', title: 'Listo', message: 'El stock quedó cargado.' },
      };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'No pudimos completar esto.';
      return {
        kind: 'blocked',
        notice: { tone: 'error', title: 'No se pudo cargar stock', message },
      };
    }
  }

  parseAdjustStock(ctx: Pick<InventoryEditContext, 'canEdit' | 'canAdjust'>, form: AdjustStockForm) {
    return parseAdjustStockForm(ctx, form);
  }

  async runAdjustStock(
    ctx: InventoryEditContext,
    form: AdjustStockForm
  ): Promise<InventoryFlowResult> {
    const parsed = this.parseAdjustStock(ctx, form);
    if (parsed.kind === 'blocked') return parsed;
    try {
      await this.port.adjustStock({
        stockId: parsed.data!.stockId,
        delta: parsed.data!.delta,
        reason: parsed.data!.reason,
        performedByUserId: ctx.userId,
      });
      return {
        kind: 'done',
        refresh: parsed.refresh,
        notice: { tone: 'success', title: 'Listo', message: 'El ajuste quedó cargado.' },
      };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'No se pudo ajustar.';
      return { kind: 'blocked', notice: { tone: 'error', title: 'Error', message } };
    }
  }

  parseCorrectStockCost(ctx: Pick<InventoryEditContext, 'canEdit' | 'canViewValuation'>, form: CorrectStockCostForm) {
    return parseCorrectStockCostForm(ctx, form);
  }

  async runCorrectStockCost(
    ctx: InventoryEditContext,
    form: CorrectStockCostForm
  ): Promise<InventoryFlowResult> {
    const parsed = this.parseCorrectStockCost(ctx, form);
    if (parsed.kind === 'blocked') return parsed;
    try {
      await this.port.correctStockAvgCost({
        productId: parsed.data!.productId,
        avgCost: parsed.data!.avgCost,
        performedByUserId: ctx.userId,
      });
      return {
        kind: 'done',
        refresh: parsed.refresh,
        notice: { tone: 'success', title: 'Costo actualizado', message: 'El costo promedio quedó guardado.' },
      };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'No se pudo actualizar el costo.';
      return { kind: 'blocked', notice: { tone: 'error', title: 'Error', message } };
    }
  }

  canOpenTransfer(ctx: InventoryEditContext & { canTransfer?: boolean; destinationBranchCount: number }) {
    return canOpenTransferEntry(ctx);
  }

  parseTransfer(ctx: InventoryEditContext & { canTransfer?: boolean; destinationBranchCount: number }, form: TransferStockForm) {
    return parseTransferStockForm(ctx, form);
  }

  async runTransfer(
    ctx: InventoryEditContext & { canTransfer?: boolean; destinationBranchCount: number },
    form: TransferStockForm
  ): Promise<InventoryFlowResult<{ waitForReceive: boolean }>> {
    const parsed = this.parseTransfer(ctx, form);
    if (parsed.kind === 'blocked') return parsed;
    try {
      await this.port.transferStock(
        {
          productId: parsed.data!.productId,
          fromBranchId: parsed.data!.fromBranchId,
          toBranchId: parsed.data!.toBranchId,
          quantity: parsed.data!.quantity,
          performedByUserId: ctx.userId,
          variantId: parsed.data!.variantId,
        },
        { waitForReceive: parsed.data!.waitForReceive }
      );
      return {
        kind: 'done',
        refresh: parsed.refresh,
        notice: parsed.data!.waitForReceive
          ? {
              tone: 'success',
              title: 'Enviado',
              message: 'Mandaste el stock. En destino tienen que recibirlo.',
            }
          : {
              tone: 'success',
              title: 'Transferencia hecha',
              message: 'El stock ya está en la otra sucursal.',
            },
        data: { waitForReceive: parsed.data!.waitForReceive },
      };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'No pudimos transferir el stock.';
      return { kind: 'blocked', notice: { tone: 'error', title: 'Error al transferir', message } };
    }
  }

  async runReceiveTransfer(ctx: InventoryEditContext, transfer: TransferRow): Promise<InventoryFlowResult> {
    if (!ctx.canEdit) {
      return {
        kind: 'blocked',
        notice: {
          tone: 'info',
          title: 'Solo lectura',
          message: 'Podés ver el stock, no recibir transferencias.',
        },
      };
    }
    try {
      await this.port.receiveTransfer(transfer.id, { performedByUserId: ctx.userId });
      return {
        kind: 'done',
        refresh: { stocks: true, pendingTransfers: true, branchActivity: true, dashboard: true },
        notice: { tone: 'success', title: 'Recibido', message: 'El stock se sumó a esta sucursal.' },
      };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'No se pudo recibir.';
      return { kind: 'blocked', notice: { tone: 'error', title: 'Error', message } };
    }
  }

  async runRejectTransfer(ctx: InventoryEditContext, transfer: TransferRow): Promise<InventoryFlowResult> {
    if (!ctx.canEdit) {
      return {
        kind: 'blocked',
        notice: {
          tone: 'info',
          title: 'Solo lectura',
          message: 'Podés ver el stock, no rechazar transferencias.',
        },
      };
    }
    try {
      await this.port.rejectTransfer(transfer.id, { performedByUserId: ctx.userId });
      return {
        kind: 'done',
        refresh: { pendingTransfers: true, branchActivity: true, dashboard: true },
        notice: { tone: 'success', title: 'Rechazado', message: 'El stock volvió a la sucursal origen.' },
      };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'No se pudo rechazar.';
      return { kind: 'blocked', notice: { tone: 'error', title: 'Error', message } };
    }
  }

  canOpenBulk(ctx: Pick<InventoryEditContext, 'canEdit'>) {
    return canOpenBulkCoefficient(ctx);
  }

  parseBulkPreview(ctx: InventoryEditContext, scope: BulkPreviewScope) {
    return parseBulkPreviewScope(ctx, scope);
  }

  async loadBulkPreview(ctx: InventoryEditContext, scope: BulkPreviewScope): Promise<InventoryFlowResult<import('../../catalog/domain-api').BulkPreviewItem[]>> {
    const parsed = this.parseBulkPreview(ctx, scope);
    if (parsed.kind === 'blocked') return parsed;
    try {
      const items = await this.port.getBulkPreview(parsed.data!.query);
      if (items.length === 0) {
        return {
          kind: 'done' as const,
          refresh: {},
          notice: { tone: 'info' as const, title: 'Sin resultados', message: 'No hay productos que coincidan con el filtro.' },
          data: items,
        };
      }
      return { kind: 'done' as const, refresh: {}, data: items };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'No se pudo cargar la vista previa.';
      return { kind: 'blocked' as const, notice: { tone: 'error' as const, title: 'Error', message } };
    }
  }

  parseBulkApply(ctx: InventoryEditContext, scope: BulkApplyScope) {
    return parseBulkApplyScope(ctx, scope);
  }

  async runBulkApply(ctx: InventoryEditContext, scope: BulkApplyScope): Promise<InventoryFlowResult<{ updated: number }>> {
    const parsed = this.parseBulkApply(ctx, scope);
    if (parsed.kind === 'blocked') return parsed;
    try {
      const meta = { performedByUserId: ctx.userId };
      const updated =
        parsed.data!.applyTo === 'prices'
          ? await this.port.bulkUpdatePrices(parsed.data!.productIds, parsed.data!.coefficient, meta)
          : await this.port.bulkUpdateQuantities(ctx.branchId!, parsed.data!.productIds, parsed.data!.coefficient, meta);
      return {
        kind: 'done',
        refresh: parsed.refresh,
        notice: {
          tone: 'success',
          title: 'Listo',
          message: `Se actualizaron ${updated} ${parsed.data!.applyTo === 'prices' ? 'precios' : 'cantidades'}.`,
        },
        data: { updated },
      };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'No se pudo aplicar.';
      return { kind: 'blocked', notice: { tone: 'error', title: 'Error', message } };
    }
  }

  canOpenPhysicalCount(ctx: InventoryEditContext & { canCount?: boolean }) {
    return canOpenPhysicalCount(ctx);
  }

  async loadPhysicalCountSession(
    ctx: InventoryEditContext,
    opts: { variantsEnabled?: boolean } = {}
  ): Promise<InventoryFlowResult<PhysicalCountRow[]>> {
    const gate = this.canOpenPhysicalCount(ctx);
    if (gate.kind === 'blocked') return gate;
    try {
      const [stockRows, catalogProducts] = await Promise.all([
        this.port.getAllStockForBranch(ctx.branchId!),
        this.loadAllCatalogProducts(),
      ]);

      let variantCellsByProductId: Map<string, VariantStockCellRow[]> = new Map();
      if (opts.variantsEnabled) {
        const cells = await Promise.all(
          catalogProducts.map((product) =>
            this.port.listVariantStockCells(ctx.branchId!, product.id).then((rows) => [product.id, rows] as const)
          )
        );
        for (const [productId, rows] of cells) {
          if (rows.length > 0) variantCellsByProductId.set(productId, rows);
        }
      }

      return {
        kind: 'done',
        refresh: {},
        data: buildPhysicalCountRows(stockRows, catalogProducts, {
          variantsEnabled: opts.variantsEnabled ?? false,
          variantCellsByProductId,
        }),
      };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'No pudimos cargar el stock.';
      return { kind: 'blocked', notice: { tone: 'error', title: 'Error', message } };
    }
  }

  parsePhysicalCountApply(ctx: InventoryEditContext, rows: PhysicalCountRow[]) {
    return parsePhysicalCountApply(ctx, rows);
  }

  async runPhysicalCountApply(ctx: InventoryEditContext, rows: PhysicalCountRow[]): Promise<InventoryFlowResult<{ applied: number }>> {
    const parsed = this.parsePhysicalCountApply(ctx, rows);
    if (parsed.kind === 'blocked') return parsed;
    try {
      const applied = await this.port.applyPhysicalCountMixedBatch(
        ctx.branchId!,
        diffPhysicalCountRows(rows),
        { performedByUserId: ctx.userId }
      );
      return {
        kind: 'done',
        refresh: parsed.refresh,
        notice: {
          tone: 'success',
          title: 'Conteo aplicado',
          message: `Ajustamos ${applied} productos con el conteo.`,
        },
        data: { applied },
      };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'No pudimos aplicar el conteo.';
      return { kind: 'blocked', notice: { tone: 'error', title: 'Error', message } };
    }
  }

  buildHistoryFilters(form: HistoryFilterForm) {
    return buildHistoryFilters(form);
  }

  async loadMovementHistoryPage(
    stock: StockRow,
    limit: number,
    offset: number,
    form: HistoryFilterForm,
    remoteOptions?: Parameters<InventoryOperationsPort['getMovementHistory']>[4]
  ) {
    return this.port.getMovementHistory(stock, limit, offset, buildHistoryFilters(form), remoteOptions);
  }

  async loadMovementHistoryExport(
    stock: StockRow,
    form: HistoryFilterForm,
    limit: number,
    remoteOptions?: Parameters<InventoryOperationsPort['getMovementHistoryForExport']>[3]
  ) {
    return this.port.getMovementHistoryForExport(stock, buildHistoryFilters(form), limit, remoteOptions);
  }

  lookupBarcode(code: string) {
    return this.port.lookupBarcode(code);
  }

  getStockPage(
    branchId: string,
    opts: GetStockByBranchOptions & { limit: number; offset: number }
  ) {
    return this.port.getStockPage(branchId, opts);
  }

  getStockByProductAndBranch(branchId: string, productId: string) {
    return this.port.getStockByProductAndBranch(branchId, productId);
  }

  getProductById(productId: string) {
    return this.port.getProductById(productId);
  }

  private async loadAllCatalogProducts() {
    const all = [];
    const pageSize = 200;
    let offset = 0;
    for (;;) {
      const batch = await this.port.searchInventoriableProducts({ limit: pageSize, offset });
      all.push(...batch);
      if (batch.length < pageSize) break;
      offset += batch.length;
    }
    return all;
  }
}

export function createInventoryOperationsController(port: InventoryOperationsPort) {
  return new InventoryOperationsController(port);
}
